import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import { fetchPatientPlans } from "../api/healthcare/TreatmentPlanApi";
import { PatientTreatmentPlan } from "../types/healthcare/TreatmentPlanTypes";
import type { UserUuid } from "../types/identity";

interface UseActiveTreatmentPlanOptions {
	patientId: UserUuid;
	appointmentTypeId?: number;
	enabled: boolean;
}

interface UseActiveTreatmentPlanResult {
	activePlan: PatientTreatmentPlan | null;
	setActivePlan: Dispatch<SetStateAction<PatientTreatmentPlan | null>>;
	reloadPlan: () => void;
	stageMatchesAppointment: boolean;
}

export function useActiveTreatmentPlan({ patientId, appointmentTypeId, enabled }: UseActiveTreatmentPlanOptions): UseActiveTreatmentPlanResult {
	const [activePlan, setActivePlan] = useState<PatientTreatmentPlan | null>(null);

	const reloadPlan = useCallback(() => {
		fetchPatientPlans(patientId)
			.then((plans) => setActivePlan(plans.find((p) => p.status === "active" || p.status === "paused") ?? null))
			.catch(() => { /* non-critical */ });
	}, [patientId]);

	useEffect(() => {
		if (!patientId || !enabled) return;
		reloadPlan();
	}, [patientId, enabled, reloadPlan]);

	const stageConfig = activePlan?.current_stage?.configuration as Record<string, any> | undefined;
	const stageTypeId = stageConfig?.appointment_type_id;
	const stageIsAppointment = activePlan?.current_stage?.stage_type === "appointment";
	const stageMatchesAppointment = !!(stageIsAppointment && stageTypeId && appointmentTypeId && Number(stageTypeId) === Number(appointmentTypeId));

	return { activePlan, setActivePlan, reloadPlan, stageMatchesAppointment };
}
